import { Component, OnInit, Input, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Company } from '../../../models/companies/company';
import { Router } from '@angular/router';
import { AppContextService } from '../../../services/app-context.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { CompanySearchCriteria } from '../../../models/companies/company-search-criteria';
import { HttpErrorResponse } from '@angular/common/http';
import { OperationResult } from '../../../models/server-result/operation-result';
import { CompanyUniverseSearch } from '../../../models/enums/company-universe-search';
import { CompanyCardDetails } from '../../../models/companies/company-card-details';
import { CompanyCard } from '../../../models/companies/company-card';
import { Observable, of, forkJoin } from 'rxjs';
import { User } from '../../../models/User/user';
import { OperationError } from '../../../models/server-result/operation-error';
import { Report } from '../../../models/reports/Report';
import { ReportingApiService } from '../../../server-api/reporting-api.service';
import { ReportingExportHelperService } from '../../../services/reporting-exports/reporting-export-helper.service';
import { TranslateService } from '../../../services/translate/translate.service';
import { CustomTableComponent } from '../../../shared-components/custom-table/custom-table.component';
import {
	TableColumn,
	TableFilter,
	TablePagination,
	TableSearchEvent,
	TableExportOption,
} from '../../../shared-components/custom-table/custom-table.types';
import {
	CustomTableFilterMapperService,
	FilterMappingConfig,
} from '../../../shared-components/custom-table/custom-table-filter-mapper.service';
import { BaseFilterableComponent } from '../../../shared-components/custom-table/base-filterable-component';
import { finalize } from 'rxjs';
import { CompanyApiService } from '../../../server-api/company-api/company-api.service';

@Component({
	selector: 'app-search-companies',
	templateUrl: './search-companies.component.html',
	styleUrls: ['./search-companies.component.scss'],
	standalone: false,
})
export class SearchCompaniesComponent extends BaseFilterableComponent implements OnInit {
	@Input() companyUniverseSearch: CompanyUniverseSearch;
	@Input() searchOnInit: boolean = true;
	@Input() onlyFinanciers: boolean = false;
	@Input() class: string = '';

	@ViewChild('customTable') customTable: CustomTableComponent;

	public companiesCards: CompanyCard[] = [];
	public companySearchCriteria: CompanySearchCriteria = new CompanySearchCriteria();
	public isResourceLoaded: boolean;
	public loggedUser: User | null = new User();
	public isPerformingRequest: boolean = false;
	public requestErrors: OperationError[];
	public isTableLoading: boolean = true;

	// Table configuration
	public tableColumns: TableColumn[] = [];
	public tableFilters: TableFilter[] = [];
	public tablePagination: TablePagination = {
		pageIndex: 0,
		pageSize: 25,
		pageSizeOptions: [],
		totalItems: 0,
	};

	protected filterMappings: FilterMappingConfig[] = [
		{
			field: 'Name',
			targetProperty: 'Name',
			type: 'string',
		},
		{
			field: 'VatNumber',
			targetProperty: 'VatNumber',
			type: 'string',
		},
	];

	protected searchCriteria = this.companySearchCriteria;

	constructor(
		private companyApiService: CompanyApiService,
		private appContext: AppContextService,
		private toasterService: ToasterService,
		private router: Router,
		private reportingApiService: ReportingApiService,
		private reportingExportHelperService: ReportingExportHelperService,
		private translateService: TranslateService,
		protected filterMapper: CustomTableFilterMapperService,
		private cdr: ChangeDetectorRef
	) {
		super();
	}

	ngOnInit() {
		this.setFilterMapper(this.filterMapper);
		this.initializeTableFilters();
		this.initializeTableColumns();

		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			this.companySearchCriteria.OnlyFinanciers = this.onlyFinanciers;
		});
	}

	private initializeTableFilters(): void {
		this.tableFilters = [
			{
				field: 'Name',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('search-companies.byName', 'client'),
			},
			{
				field: 'VatNumber',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('search-companies.byVatNumber', 'client'),
			},
		];
	}

	private initializeTableColumns(): void {
		// Columns for table view (hidden for now, but prepared for future use)
		this.tableColumns = [
			{
				field: 'Company.Name',
				header: 'search-companies.byName',
				render: (row: CompanyCard) => row.Company?.Name || '-',
			},
			{
				field: 'Company.VatNumber',
				header: 'search-companies.byVatNumber',
				render: (row: CompanyCard) => `${row.Company?.CountryCode || ''} ${row.Company?.VatNumber || ''}`,
			},
			{
				type: 'actions',
				width: '80px',
				isMenu: true,
				icon: 'fa-solid fa-ellipsis-v',
				hideInCardView: true,
				menuItems: [
					{
						label: this.translateService.translate('company.card.manage.relations', 'client'),
						icon: 'fa-light fa-chart-network',
						action: (row: CompanyCard) => this.router.navigate(['/private/company/relations', row.Company?.Id]),
						condition: (row: CompanyCard) => this.userCanSeeCompanyRelations(row),
					},
					{
						label: this.translateService.translate('search-companies.users', 'client'),
						icon: 'fa-light fa-users-class',
						action: (row: CompanyCard) => this.router.navigate(['/private/company', row.Company?.Id, 'users']),
						condition: (row: CompanyCard) => this.userCanSeeCompanyUsers(row),
					},
					{
						label: this.translateService.translate('company.card-company-details', 'client'),
						icon: 'fa-light fa-ellipsis-h-alt',
						action: (row: CompanyCard) => this.router.navigate(['/private/company/details', row.Company?.Id]),
						condition: (row: CompanyCard) => this.userCanViewCompanyInformation(row),
					},
					{
						label: this.translateService.translate('search-companies.view.contracts', 'client'),
						icon: 'fa-light fa-file-contract',
						action: (row: CompanyCard) => this.router.navigate(['/private/contracts/search', row.Company?.Id]),
						condition: (row: CompanyCard) => this.userCanAccessContracts(row),
					},
				],
			},
		];
	}

	public tableExport: TableExportOption = {
		label: this.translateService.translate('search-companies.exportRecords', 'client'),
		action: () => this.exportNetwork(),
		condition: () => this.canExportCompanySearchResults(),
	};

	public onSearchRequested(event: TableSearchEvent): void {
		this.companySearchCriteria = new CompanySearchCriteria();
		this.companySearchCriteria.OnlyFinanciers = this.onlyFinanciers;
		this.searchCriteria = this.companySearchCriteria;

		if (this.filterMapper && event.filters?.length > 0) {
			this.filterMapper.mapFiltersToObject(event.filters, this.filterMappings, this.searchCriteria);
		}

		this.tablePagination.pageIndex = event.pageIndex;
		this.tablePagination.pageSize = event.pageSize;

		// API expects 1-based page numbers, custom-table uses 0-based pageIndex
		const pageNumber = event.pageIndex + 1;
		this.performSearch(pageNumber);
	}

	public getCompanyLogoUrl(): string {
		return './assets/images/company-dummy-3.png';
	}

	public performSearch(pageNumber: number) {
		this.tablePagination.pageIndex = pageNumber - 1;
		this.isTableLoading = true;
		this.isPerformingRequest = true;

		const criteria = new CompanySearchCriteria();
		criteria.OrderBy = 'Name';
		criteria.OrderDescending = false;
		criteria.PageNumber = pageNumber;
		criteria.RowsPerPage = this.tablePagination.pageSize;
		criteria.RestrictionCriteria = this.companySearchCriteria;
		criteria.RestrictionCriteria.CompanyUniverseSearch = this.companyUniverseSearch;

		let servicesCallsToMake: Array<Observable<OperationResult<Company[] | number>>> = [];

		servicesCallsToMake.push(this.companyApiService.searchCompanies(criteria));
		servicesCallsToMake.push(this.companyApiService.countSearchCompanies(criteria.RestrictionCriteria));

		const getAllRequests = forkJoin(servicesCallsToMake);

		getAllRequests.subscribe({
			next: (opResults) => {
				let searchCompaniesResult = opResults[0] as OperationResult<Company[]>;
				let countSearchCompaniesResult = opResults[1] as OperationResult<number>;

				if (!searchCompaniesResult.IsValid) {
					this.processInValidSearchCompaniesResult();
					this.isTableLoading = false;
					this.isPerformingRequest = false;
					return;
				}

				if (!countSearchCompaniesResult.IsValid) {
					this.processInValidCountSearchCompaniesResult();
					this.isTableLoading = false;
					this.isPerformingRequest = false;
					return;
				}

				this.tablePagination.totalItems = countSearchCompaniesResult.Data || 0;
				this.tablePagination = { ...this.tablePagination };

				this.processValidSearchCompaniesResult(searchCompaniesResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				this.isTableLoading = false;
				this.isPerformingRequest = false;
			},
		});
	}

	private processValidSearchCompaniesResult(searchCompaniesOperationResult: OperationResult<Company[]>) {
		let companies: Company[] = searchCompaniesOperationResult.Data;

		this.companiesCards = [];

		if (!!this.companyUniverseSearch) {
			let servicesCallsToMake: Array<Observable<OperationResult<CompanyCardDetails>>> = [];

			for (let index = 0; index < companies.length; index++) {
				let company = companies[index];

				if (company?.Id) servicesCallsToMake.push(this.getCompanyCardDetailsServiceCall(company.Id!));
			}

			if (servicesCallsToMake.length == 0) {
				this.isResourceLoaded = true;
				this.isTableLoading = false;
				this.isPerformingRequest = false;
				this.cdr.markForCheck();
				this.customTable?.triggerFinalRerender();
				return;
			}

			const getAllRequests = forkJoin(servicesCallsToMake);

			getAllRequests
				.pipe(
					finalize(() => {
						this.isResourceLoaded = true;
						this.isTableLoading = false;
						this.isPerformingRequest = false;
						this.companiesCards = [...this.companiesCards];
						this.cdr.markForCheck();
						this.customTable?.triggerFinalRerender();
					})
				)
				.subscribe({
					next: (opResults) => {
						for (let index = 0; index < servicesCallsToMake.length; index++) {
							let getCompanyCardDetails = opResults[index] as OperationResult<CompanyCardDetails>;

							if (!getCompanyCardDetails.IsValid) {
								const errors = getCompanyCardDetails.Errors;
								if (errors && errors.length > 0) {
									this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
										errors: errors,
										autoClose: false,
									});
								}
							} else {
								if (!!getCompanyCardDetails.Data) {
									let connectedCompanyCard: CompanyCard = <CompanyCard>{
										Company: companies.find((c) => c.Id === getCompanyCardDetails.Data.CardCompanyId),
										CompanyCardDetails: getCompanyCardDetails.Data,
									};

									if (
										this.companyUniverseSearch == CompanyUniverseSearch.Network &&
										!this.loggedUser?.IsSystemAdmin &&
										!this.loggedUser?.IsSupportOperator &&
										!this.loggedUser?.IsBackOfficeOperator
									) {
										if (!!connectedCompanyCard && !!connectedCompanyCard.CompanyCardDetails)
											connectedCompanyCard.CompanyCardDetails.IsFinancier = false;
									}
									this.companiesCards.push(connectedCompanyCard);
								} else {
									this.processInvalidConnectedCompanyCardDetailsResult();
								}
							}
						}

						this.isPerformingRequest = false;
					},
					error: (httpErrorResponse: HttpErrorResponse) => {
						console.error('error: ' + httpErrorResponse.message);
						this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
					},
				});
		} else {
			companies.forEach((company: Company) => {
				let companyCard: CompanyCard = <CompanyCard>{
					Company: company,
					CompanyCardDetails: null,
				};

				this.companiesCards.push(companyCard);
			});

			this.isResourceLoaded = true;
			this.isTableLoading = false;
			this.isPerformingRequest = false;
			this.companiesCards = [...this.companiesCards];
			this.cdr.markForCheck();
			this.customTable?.triggerFinalRerender();
		}
	}

	private getCompanyCardDetailsServiceCall(companyId: string): Observable<OperationResult<CompanyCardDetails>> {
		let loggedUserCompanies = this.loggedUser?.Companies.map((c) => c.Id!) ?? [];

		if (this.companyUniverseSearch == CompanyUniverseSearch.MyConnections)
			return this.companyApiService.getConnectedCompanyCardDetails(companyId, loggedUserCompanies);
		else if (this.companyUniverseSearch == CompanyUniverseSearch.MyCompanies)
			return this.companyApiService.getMyCompanyCardDetails(companyId);
		else if (this.companyUniverseSearch == CompanyUniverseSearch.Network)
			return this.companyApiService.getNetworkCompanyCardDetails(companyId, loggedUserCompanies);

		return of<OperationResult<CompanyCardDetails>>(new OperationResult<CompanyCardDetails>());
	}

	private processInvalidConnectedCompanyCardDetailsResult(): any {
		this.toasterService.showError(this.translateService.translate('search-companies.invalid-result-from-server-companies', 'client'));
	}

	private processInValidSearchCompaniesResult() {
		this.toasterService.showError(this.translateService.translate('search-companies.invalid-result-from-server-companies', 'client'));
	}

	private processInValidCountSearchCompaniesResult() {
		this.toasterService.showError(
			this.translateService.translate('search-companies.errors-returnd-when-calling-count-companies-resultfalse', 'client')
		);
	}

	public redirectToExistingSupplierInvitationStep1(companyCard: CompanyCard): void {
		let invitationContext = btoa('invitedCompanyId:' + companyCard.Company.Id);
		this.router.navigate(['/private/invitation/confirming/company/new/supplier/step1', invitationContext]);
	}

	private inThisCompany(companyId): boolean {
		if (this.loggedUser?.Companies) {
			return !!this.loggedUser.Companies.find((c) => c.Id === companyId);
		}
		return false;
	}

	public userCanViewCompanyInformation(companyCard: CompanyCard): boolean {
		if (this.loggedUser?.IsSystemAdmin) return true;

		if (this.loggedUser?.IsSupportOperator) return true;

		if (this.loggedUser?.IsBackOfficeOperator) return true;

		if (this.inThisCompany(companyCard.Company.Id)) return true;

		return false;
	}

	public userCanSeeCompanyUsers(companyCard: CompanyCard): boolean {
		if (this.loggedUser?.IsSystemAdmin) return true;

		if (this.loggedUser?.IsSupportOperator) return true;

		if (this.loggedUser?.IsBackOfficeOperator) return true;

		if (this.inThisCompany(companyCard.Company.Id)) return true;

		return false;
	}

	public userCanSeeCompanyRelations(companyCard: CompanyCard): boolean {
		let isBuyer = this.loggedUser?.IsBuyer;
		let isSupplier = this.loggedUser?.IsSupplier;
		let isInCompany = this.inThisCompany(companyCard.Company.Id);

		if (this.loggedUser?.IsSystemAdmin || this.loggedUser?.IsSupportOperator || this.loggedUser?.IsBackOfficeOperator) {
			return true;
		} else if ((isBuyer || isSupplier) && isInCompany) {
			return true;
		} else if (this.loggedUser?.IsFinancier && companyCard.CompanyCardDetails?.IsBuyer) {
			return true;
		}

		return false;
	}

	public userCanAccessContracts(companyCard: any): boolean {
		return !!(
			companyCard?.Company?.IsFinancier &&
			(this.loggedUser?.IsSystemAdmin || this.loggedUser?.IsSupportOperator || this.loggedUser?.IsBackOfficeOperator)
		);
	}

	public canExportCompanySearchResults(): boolean {
		return this.tablePagination.totalItems > 0;
	}

	public exportNetwork() {
		this.isPerformingRequest = true;

		this.reportingApiService.exportCompaniesNetwork(this.companySearchCriteria).subscribe({
			next: (operationResult: OperationResult<Report>) => {
				if (operationResult.IsValid && operationResult.Data) {
					this.reportingExportHelperService.processValidReport(operationResult.Data);
				} else {
					this.reportingExportHelperService.processInvalidReport(operationResult, 'Invalid result from server export companies network');
				}

				this.isPerformingRequest = false;
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
			complete: () => {
				this.tableExport = { ...this.tableExport, exporting: false };
				this.cdr.detectChanges();
			},
		});
	}

	public hasFooterActions(row: CompanyCard): boolean {
		return (
			this.userCanSeeCompanyRelations(row) ||
			this.userCanSeeCompanyUsers(row) ||
			this.userCanViewCompanyInformation(row) ||
			this.userCanAccessContracts(row)
		);
	}
}
